import React, { ChangeEvent } from 'react'
import { Button } from '@/components/ui/button'
import { LabeledValue } from 'antd/es/select'
import { TeamDto } from 'api/Identity/Types/Team'
import { UpsertUserTeam, UpsertUserUserTeams } from 'api/Identity/Types/UpsertUserUserTeams'
import { UserTeamDto } from 'api/Identity/Types/UserTeamDto'
import {
  FormBaseButtonContainer,
  FormBaseContainer,
  FormBaseFieldBlock,
  FormBaseRow,
} from 'components/Form/Form.styles'
import { MultiSelect, Option } from 'components/Select'
import { Form, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

import { FormFieldGroup, FormFieldLabel } from '../Administration.styles'

interface AdministrationAddUserTeamFormValues {
  teams: LabeledValue[]
  eligibleToClientAutoAssignment?: boolean
  visibility?: boolean
}

const AdministrationAddUserTeamFormUI: React.FC<
  FormikProps<AdministrationAddUserTeamFormValues> & OuterProps
> = (props) => {
  const { values, handleSubmit, setFieldValue, isSubmitting, data } = props

  return (
    <FormBaseContainer>
      <Form onSubmit={handleSubmit}>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormFieldLabel>Teams</FormFieldLabel>
            <FormFieldGroup>
              <MultiSelect
                labelInValue={true}
                placeholder='Teams'
                value={values.teams}
                onChange={(labeledValues: LabeledValue[]) =>
                  props.setFieldValue('teams', labeledValues)
                }
                fullWidth
              >
                <Option value={''}>Not set</Option>
                {props.teams
                  .filter((team) => !data.userData?.teams.map((x) => x).includes(team.name))
                  .map((team) => (
                    <Option value={team.id} key={team.id}>
                      {team.name}
                    </Option>
                  ))}
              </MultiSelect>
            </FormFieldGroup>
          </FormBaseRow>
          <FormBaseRow>
            <FormBaseRow>
              <FormFieldLabel>Auto-Assignment</FormFieldLabel>
              <FormFieldGroup>
                <input
                  name='eligibleToClientAutoAssignment'
                  type='checkbox'
                  checked={values.eligibleToClientAutoAssignment}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setFieldValue('eligibleToClientAutoAssignment', event.target.checked)
                  }}
                />
              </FormFieldGroup>
            </FormBaseRow>
          </FormBaseRow>
          <FormBaseRow>
            <FormBaseRow>
              <FormFieldLabel>Visibility</FormFieldLabel>
              <FormFieldGroup>
                <input
                  name='visibility'
                  type='checkbox'
                  checked={values.visibility}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    setFieldValue('visibility', event.target.checked)
                  }}
                />
              </FormFieldGroup>
            </FormBaseRow>
          </FormBaseRow>
        </FormBaseFieldBlock>
        <FormBaseButtonContainer>
          <Button disabled={isSubmitting} type='submit'>
            Save
          </Button>
        </FormBaseButtonContainer>
      </Form>
    </FormBaseContainer>
  )
}

const dataToSubmit = (
  values: AdministrationAddUserTeamFormValues
): FormSubmitValues<UpsertUserUserTeams> => {
  const parsedTeams = values.teams.map(
    (teamToParse: LabeledValue): UpsertUserTeam => ({
      TeamId: teamToParse.value as string,
      EligibleToClientAutoAssignment: values.eligibleToClientAutoAssignment || false,
      Visibility: values.visibility || false,
    })
  )
  return {
    UserTeams: parsedTeams,
  }
}

interface OuterProps {
  data: UserTeamDto
  teams: TeamDto[]
  onSubmit(values: FormSubmitValues<UpsertUserUserTeams>): Promise<void>
  values?: FormSubmitValues<UpsertUserUserTeams>
}

export const AdministrationAddUserTeamForm = withFormik<
  OuterProps,
  AdministrationAddUserTeamFormValues
>({
  mapPropsToValues: ({ teams, values }) => {
    if (values)
      return {
        teams:
          values.UserTeams?.map((team) => ({
            value: team.TeamId,
            label: teams.find((newTeam) => newTeam.id === team.TeamId)?.name ?? '',
          })) ?? [],
        eligibleToClientAutoAssignment:
          values.UserTeams?.[0].EligibleToClientAutoAssignment ?? false,
        visibility: values.UserTeams?.[0].Visibility ?? false,
      }
    return {
      teams: [],
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    await props.onSubmit(dataToSubmit(values))
    setSubmitting(false)
  },
  isInitialValid: false,
  enableReinitialize: true,
})(AdministrationAddUserTeamFormUI)

export default AdministrationAddUserTeamForm
